import { mapActions, mapGetters } from 'vuex';
import {
    EVENTS,
    TrackingService,
} from '@/core/services/TrackingService/TrackingService.js';
import SecureApi from '@/flows/Authentication/services/SecureApi.js';
import CONSTANTS from '@/core/helpers/constants';

export const QuitGame = {
    computed: {
        ...mapGetters([
            'getGameCode',
            'getGameMode',
            'getClientType',
            'isSelfPlaying',
            'isHomeGame',
            'user',
        ]),
    },
    methods: {
        ...mapActions([
            'resetStoreState',
            'getGameTypes',
            'stopAudio',
            'getReports',
        ]),
        async transferGameToMongoAndCleanupRedis(gameMode, gameCode) {
            // only for live games as SPGs quitting does not mean end
            if (gameMode === 'live') {
                void SecureApi().post(
                    `game/quit-game-redis-cleanup/${gameCode}`,
                );
            }
        },
        async cleanUp() {
            localStorage.removeItem('playedTime');
            await this.stopAudio();
            await this.resetStoreState();
        },
        async quitGame(
            askConfirmation = false,
            location = null,
            routeToRedirectTo = '',
        ) {
            this.$store.commit('v2/game/tbl/quitConfirmed', !askConfirmation);

            if (askConfirmation) {
                const quitConfirm = await this.$store.dispatch(
                    'v2/ui/confirm',
                    'Are you sure?',
                );

                if (quitConfirm) {
                    this.$store.commit('v2/game/tbl/quitConfirmed', true);
                } else {
                    return;
                }
            }

            const redirectBack = (toName) => {
                const to = {
                    name: routeToRedirectTo || toName,
                };
                if (routeToRedirectTo === 'host.create-game.configure') {
                    to.params = {
                        gameType: 'TYPE_ADDITION',
                    };
                }
                return this.$router.push(to);
            };

            if (this.$store.state.v2.game.mode === CONSTANTS.LIVE_GAME_MODE) {
                await this.$store.dispatch('v2/game/leave');

                if (this.$isTeacher) {
                    console.debug('QUIT GAME. EXIT 1');
                    return redirectBack('host');
                }

                console.debug('QUIT GAME. EXIT 2');
                return this.$router.push({ name: 'home' });
            }

            // Set these temp variables, because otherwise the store will be wiped.
            const clientType = this.getClientType;
            const gameCode = this.getGameCode;
            const gameMode = this.getGameMode;
            const isSelfPlaying = this.isSelfPlaying;
            const isHomeGame = this.isHomeGame;

            await this.cleanUp();
            await this.getGameTypes();

            // removes player from live game when quits
            if (
                gameMode === CONSTANTS.LIVE_GAME_MODE &&
                clientType === CONSTANTS.CLIENT_TYPES.PLAYER &&
                this.$route.name === 'livegame.preview'
            ) {
                const playerName =
                    this.$store.getters.getPlayerName ||
                    sessionStorage.getItem('playerName');

                this.$socket.client.emit('removePlayer', {
                    gameCode,
                    playerName,
                    playerExited: true,
                });
            }

            if (clientType === CONSTANTS.CLIENT_TYPES.HOST) {
                await this.transferGameToMongoAndCleanupRedis(
                    gameMode,
                    gameCode,
                );
            }

            if (
                clientType === CONSTANTS.CLIENT_TYPES.HOST ||
                isSelfPlaying ||
                isHomeGame
            ) {
                const routeName = this.$route.name;

                if (
                    routeName === 'host.lobby' ||
                    routeName === 'host.preview' ||
                    routeName === 'host.play'
                ) {
                    this.$socket.client.emit('hostLeftGame', gameCode);
                }

                this.$store.commit(
                    'setClientType',
                    CONSTANTS.CLIENT_TYPES.HOST,
                );

                if (location === 'gameEndBtn') {
                    console.debug('QUIT GAME. EXIT 3');
                    return this.$router.push({ name: 'host' });
                } else {
                    const trackingService = new TrackingService(this.user);

                    if (location === 'logo') {
                        trackingService.track(EVENTS.CLICKED_ON_LOGO, {
                            gameCode: this.getGameCode,
                            routeName,
                        });
                    } else {
                        trackingService.track(EVENTS.CLICKED_QUIT_GAME, {
                            gameCode: this.getGameCode,
                            routeName,
                        });
                        // Avoid the NavigationDuplicated error.
                        // Business need is that the quit game btn click
                        // still takes to dashboard.
                        if (this.$route.name !== 'host') {
                            console.debug('QUIT GAME. EXIT 4');
                            return this.$router.push({ name: 'host' });
                        }
                    }
                }
            }

            if (routeToRedirectTo === '') {
                // Avoid the NavigationDuplicated error.
                if (this.$route.name !== 'join') {
                    console.debug('QUIT GAME. EXIT 5');
                    return this.$router.push({ name: 'join' });
                }
            } else if (routeToRedirectTo !== null) {
                if (this.$route.name !== routeToRedirectTo) {
                    console.debug('QUIT GAME. EXIT 6');
                    return redirectBack(routeToRedirectTo);
                }
            }

            if (!this.user && this.$route.name !== 'home') {
                // Avoid the NavigationDuplicated error.
                console.debug('QUIT GAME. EXIT 7');
                return this.$router.push({ name: 'home' });
            }
        },
        /**
         * This is needed to make sure that the store gets cleared as well,
         * when moving between reports.
         *
         * @returns {Promise<void>}
         */
        async quitGameToReports() {
            // Set these temp variables, because otherwise the store will be wiped.
            const clientType = this.getClientType;

            await this.cleanUp();

            if (clientType === 'host') {
                this.$store.commit(
                    'setClientType',
                    CONSTANTS.CLIENT_TYPES.HOST,
                );

                this.getGameTypes();
            }
            // Avoid the NavigationDuplicated error.
            if (this.$router.name !== 'host.reports') {
                return this.$router.push({ name: 'host.reports' });
            }
        },

        async playAgain() {
            await this.cleanUp();

            console.log('play again: ');

            return this.$router.push({ name: 'join' });
        },
    },
};
